<script setup lang="ts">
import _ from "lodash";
import {storeToRefs} from "pinia";
import {computed, defineExpose, inject, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {
  buildRecursivelySectorTree,
  type LevelCollection,
} from "@oplit/shared-module";
import FButton from "@/lib/components/ui/FButton.vue";
import FDropdown from "@/lib/components/ui/FDropdown.vue";
import FTagInput from "@/lib/components/ui/FTagInput.vue";
import {FDropdownListPerimeters} from "@/components/Global";
import {useParametersStore} from "@/domains/parameters/stores/parametersStore";
import {useMainStore} from "@/stores/mainStore";
import {useSchedulingStore} from "@/stores/schedulingStore";
import {useSectors} from "@/composables/useSectors";
import useSimulationDataExport from "@/composables/useSimulationDataExport";
import {
  EMPTY_SECTORS_PLACEHOLDER_ACTIVATOR_ID,
  USER_PARAMETERS_SELECTED_SECTORS_ON_GOING_KEY,
  USER_PARAMETERS_SELECTED_SECTORS_KEY,
} from "@/config/constants";
import {MachineCenter, SectorGroup, Segment} from "@/interfaces";

interface SectorsSelectorProps {
  fullSize?: boolean;
  small?: boolean;
  onlyDropdown?: boolean;
  disabled?: boolean;
  selectedMachineProp?: {
    secteur_id: string;
    secteur_collection: LevelCollection;
  };
  canSelectParent?: boolean;
  // determines if the defaultPath should match any eventual tag
  openFirstSelected?: boolean;
  zIndex?: number;
  maxFieldHeight?: string;
  contentClass?: string | string[];
  isOngoingView?: boolean;
  canSelectAll?: boolean;
  canSelectMultiple?: boolean;
  useSimpleUpdateEventInModal?: boolean;
  title?: string;
}

const props = withDefaults(defineProps<SectorsSelectorProps>(), {
  fullSize: false,
  small: false,
  onlyDropdown: false,
  disabled: false,
  selectedMachineProp: () => ({}),
  canSelectParent: false,
  openFirstSelected: false,
  zIndex: 2000,
  maxFieldHeight: "300px",
  contentClass: () => [],
  isOngoingView: false,
  canSelectAll: true,
  canSelectMultiple: true,
  useSimpleUpdateEventInModal: false,
  title: "",
});

const emit = defineEmits<{
  (e: "update-sector", sector: MachineCenter): void;
}>();

const segment = inject<Segment>("segment");

const mainStore = useMainStore();
const {
  clientParameters,
  machines,
  perimeters,
  stations,
  activePerim,
  userParameters,
  locallyUserParameters,
  stationsAndMachinesTags,
  stationsAndMachinesTagsGroupedById,
} = storeToRefs(mainStore);
const {schedulingMachineCentersAndTags} = storeToRefs(useSchedulingStore());
const {getUsersSelectedSchedulingSectors} = useSchedulingStore();
const {parametersSectorGroups} = storeToRefs(useParametersStore());

const {t} = useI18n();

const {onSelectAllSectors} = useSectors();
const {setCurrentActiveSchedulingCenters} = useSimulationDataExport();

const machineCentersList = ref<MachineCenter[]>([]);
const open = ref(false);
const loading = ref(false);

/**
 * stores the original configuration of the user's selected sectors/groups
 */
const originalItems = ref<{
  groups: SectorGroup["id"][];
  sectors: MachineCenter["id"][];
}>({
  groups: [],
  sectors: [],
});
/**
 * stores the updated version, prior validation
 */
const selectedItems = ref<{
  groups: SectorGroup["id"][];
  sectors: MachineCenter["id"][];
}>({
  groups: [],
  sectors: [],
});

const populatedSelectedSectorsGroups = computed(() =>
  getPopulatedSectorsGroups(selectedItems.value.groups),
);
const populatedOriginalSectorsGroups = computed(() =>
  getPopulatedSectorsGroups(originalItems.value.groups),
);
const buttonText = computed(() => {
  if (populatedOriginalSectorsGroups.value.length > 0) {
    return [...populatedOriginalSectorsGroups.value]
      .sort((a, b) => b.sectors.length - a.sectors.length)
      .map(({name}) => name)
      .join(", ");
  }

  return t("Commons.sectors_dropdown_button_text", {
    count: originalItems.value.sectors.length,
  });
});
const buttonTextHelper = computed(() =>
  populatedOriginalSectorsGroups.value.length > 0
    ? `(${
        populatedOriginalSectorsGroups.value.map(({sectors}) => sectors).flat()
          .length
      } sect.)`
    : "",
);
const buttonTooltip = computed(() =>
  [buttonText.value, buttonTextHelper.value].filter(Boolean).join(" "),
);

const computedSelectedMachineProp = computed(() => {
  const selectedTag = stationsAndMachinesTags.value.find(
    (p: {id?: string}) => p.id === props.selectedMachineProp?.secteur_id,
  );

  return selectedTag?.parent_text || selectedTag?.name || "";
});
const computedSelectedMachine = computed(() => {
  if (!selectedItems.value.sectors.length)
    return computedSelectedMachineProp.value;

  const selectedCenter = schedulingMachineCentersAndTags.value.find(
    ({secteur_id}) => selectedItems.value.sectors.includes(secteur_id),
  );

  if (selectedCenter?.is_machine) {
    return machines.value.find(
      (m: {id: string}) => m.id === selectedCenter?.secteur_id,
    )?.name;
  }

  return (
    stations.value.find(
      (p: {id?: string}) => p.id === selectedCenter?.secteur_id,
    )?.parent_text || ""
  );
});
const isDisabledConfirmButton = computed(() => {
  if (!props.selectedMachineProp?.secteur_id) return false;

  return selectedItems.value.sectors.length !== 1;
});
const computedTags = computed(() => {
  const groups = populatedSelectedSectorsGroups.value.map((group) => ({
    ...group,
    // this key is used in FTagInput to distinguish the display of groups and sectors
    is_group: true,
  }));

  const sectors = selectedItems.value.sectors.map((sectorId) => {
    const [sectorOrMachine] =
      stationsAndMachinesTagsGroupedById.value[sectorId] || [];

    return sectorOrMachine;
  });

  return [...groups, ...sectors].filter(Boolean);
});
const selectedMachinePath = computed(() => {
  if (!props.selectedMachineProp || !perimeters.value) return {};
  const {secteur_id = "operations", secteur_collection} =
    props.selectedMachineProp;
  const match = (perimeters.value[secteur_collection] || []).find(
    ({id}: {id: string}) => id === secteur_id,
  );
  return buildRecursivelySectorTree(match, perimeters.value);
});
// returns either activePerim or the first tag to set for default opened path within the tree view
const treeDefaultPath = computed(() => {
  if (!props.openFirstSelected || !computedTags.value.length) {
    return props.useSimpleUpdateEventInModal
      ? selectedMachinePath.value
      : activePerim.value;
  }

  return computedTags.value[0];
});

function initMachineCenters() {
  if (props.selectedMachineProp?.secteur_id) {
    const sectors = [props.selectedMachineProp.secteur_id];
    const groups = [];

    selectedItems.value = {sectors, groups};
    originalItems.value = {sectors, groups};
  } else {
    const {allSelectedSectorsPopulated, sectors, groups} =
      getUsersSelectedSchedulingSectors(props.isOngoingView);

    machineCentersList.value = schedulingMachineCentersAndTags.value.map(
      (machineCenterAndTag) => {
        const {secteur_id, is_machine} = machineCenterAndTag;
        if (is_machine) return machineCenterAndTag;
        const match = stations.value.find(({id}) => id === secteur_id);
        if (!match) return machineCenterAndTag;
        const {text, parent_text} = match;
        return {...machineCenterAndTag, text, parent_text};
      },
    );

    selectedItems.value = {sectors, groups};
    originalItems.value = {sectors, groups};

    setCurrentActiveSchedulingCenters(
      allSelectedSectorsPopulated.map(({id}) => id),
    );
  }
}
function removeSelectedSectorsFromConwipSectorsGroups() {
  if (!clientParameters.value.has_conwip) return;

  const selectedConwipGroupsIDs = populatedSelectedSectorsGroups.value
    .filter(({is_conwip}) => is_conwip)
    .map(({id}) => id);

  if (selectedConwipGroupsIDs.length === 0) return;

  selectedItems.value.groups = selectedItems.value.groups.filter(
    (groupID) => !selectedConwipGroupsIDs.includes(groupID),
  );
}
function selectMachine(
  sector: {id?: string; secteur_id?: string},
  remove: boolean,
) {
  const {id, secteur_id} = sector || {};
  if (!id && !secteur_id) return;

  const sectorID = secteur_id || id;

  removeSelectedSectorsFromConwipSectorsGroups();

  if (props.canSelectMultiple) {
    selectedItems.value.sectors = remove
      ? selectedItems.value.sectors.filter((id: string) => id !== sectorID)
      : _.uniq([...selectedItems.value.sectors, sectorID]);
  } else selectedItems.value.sectors = remove ? [] : [sectorID];
}
function selectSectorGroup(group: SectorGroup, remove: boolean) {
  if (remove) {
    selectedItems.value.groups = selectedItems.value.groups.filter(
      (groupID) => groupID !== group.id,
    );
  } else {
    if (group.is_conwip) {
      selectedItems.value = {
        groups: [group.id],
        sectors: [],
      };

      save();
    } else {
      selectedItems.value.groups = [
        ...selectedItems.value.groups.filter((groupID) => {
          const {is_conwip} =
            populatedSelectedSectorsGroups.value.find(
              ({id}) => id === groupID,
            ) || {};
          return !is_conwip;
        }),
        group.id,
      ];
    }
  }
}

async function save() {
  loading.value = true;

  const userParameterKey = props.isOngoingView
    ? USER_PARAMETERS_SELECTED_SECTORS_ON_GOING_KEY
    : USER_PARAMETERS_SELECTED_SECTORS_KEY;

  // Key has to be removed to correctly take into account
  // what user want to display
  // locallyUserParameters allow to overwrite its parameters
  delete locallyUserParameters.value[userParameterKey];

  if (
    !_.isEqual(selectedItems.value, userParameters.value?.[userParameterKey])
  ) {
    await mainStore.setUserParameter({
      [userParameterKey]: selectedItems.value,
    });

    segment.value.track("[Ordo] Machine Centers Updated", {
      is_piloting: props.isOngoingView,
      ...selectedItems.value,
    });
  }
  loading.value = false;
  open.value = false;
}
function getPopulatedSectorsGroups(groups: string[]): SectorGroup[] {
  return groups
    .map((groupID) =>
      parametersSectorGroups.value.find(({id}) => groupID === id),
    )
    .filter(Boolean);
}
function deselectAll(): void {
  selectedItems.value = {
    sectors: [],
    groups: [],
  };
}
function onSelectAll(...args: any) {
  onSelectAllSectors(selectMachine, ...args, false, {
    canSelectMachines: true,
    canSelectParents: true,
  });
}
function handleReset(): void {
  deselectAll();
  open.value = false;
}
function handleConfirm(): void {
  props.useSimpleUpdateEventInModal ? returnSelectedMachine() : save();
}
function returnSelectedMachine(value?: {id?: string}): void {
  let selectedCenter = value || {};

  if (!props.onlyDropdown) {
    const [selectedCenterID] = selectedItems.value.sectors;
    selectedCenter = {id: selectedCenterID};
  }

  open.value = false;

  emit(
    "update-sector",
    schedulingMachineCentersAndTags.value.find(
      ({secteur_id}) => secteur_id === selectedCenter.id,
    ),
  );
}
// exposed for usage through refs
function openMenu(): void {
  open.value = true;
}

watch(
  () => [
    userParameters.value[USER_PARAMETERS_SELECTED_SECTORS_KEY],
    userParameters.value[USER_PARAMETERS_SELECTED_SECTORS_ON_GOING_KEY],
    locallyUserParameters.value[USER_PARAMETERS_SELECTED_SECTORS_KEY],
    locallyUserParameters.value[USER_PARAMETERS_SELECTED_SECTORS_ON_GOING_KEY],
    open.value,
  ],
  () => initMachineCenters(),
  {immediate: true, deep: true},
);

defineExpose({openMenu});
</script>

<template>
  <span class="fd-flex-center">
    <span v-if="onlyDropdown" :class="{'w-100': fullSize}">
      <FDropdown
        :class="{'w-100': fullSize}"
        :content-class="contentClass"
        item-value="id"
        item-text="parent_text"
        :btn-class="['cursor-pointer', {'w-100': fullSize}]"
        type="perimeter"
        :default-path="selectedMachinePath"
        :z-index="zIndex"
        single-select
        outlined
        input
        hide-chevron
        display-machine-tags
        :disabled="disabled"
        :field-text="computedSelectedMachineProp"
        :max-field-height="maxFieldHeight"
        :placeholder="$t('scheduling.machine_centers')"
        :on-change="(s, l, f, r) => returnSelectedMachine(s)"
      />
    </span>

    <v-dialog
      v-else
      v-model="open"
      :disabled="disabled"
      :z-index="zIndex"
      width="50%"
      height="80%"
    >
      <template v-slot:activator="{props}">
        <slot v-bind="props" name="activator">
          <span v-bind="props" class="d-flex text-subtitle-1">
            <FButton
              v-tooltip="buttonTooltip"
              :id="EMPTY_SECTORS_PLACEHOLDER_ACTIVATOR_ID"
              :disabled="disabled"
              outlined
              data-testid="machine-center-selector"
            >
              <template
                v-if="populatedOriginalSectorsGroups.length > 0"
                #icon-left
              >
                <OpalIcon
                  stroke="rgb(var(--v-theme-newSubText))"
                  name="sector"
                />
              </template>
              <template
                v-else-if="
                  !canSelectMultiple && selectedMachineProp?.is_machine
                "
                #icon-left
              >
                <OpalIcon stroke="rgb(var(--v-theme-newSubText))" name="tool" />
              </template>

              <span class="sectors-selector__button-text text-ellipsis">
                {{
                  canSelectMultiple ? buttonText : computedSelectedMachineProp
                }}
              </span>

              <span
                v-if="
                  canSelectMultiple &&
                  populatedOriginalSectorsGroups.length === 1
                "
                class="text-newDisableText fbody-2 pl-1"
              >
                {{ buttonTextHelper }}
              </span>
            </FButton>
          </span>
        </slot>
      </template>

      <v-card class="pa-4 sectors-selector__card">
        <div
          v-if="title || !!computedTags.length"
          class="d-flex flex-column gap-4"
        >
          <h4
            v-if="title"
            :class="{'mb-2': !!computedTags.length}"
            class="display-18 semi-bold"
          >
            {{ title }}
          </h4>
          <template v-if="!!computedTags.length">
            <p v-if="canSelectMultiple" class="text-neutral-tertiary">
              {{
                $t("Commons.selected_sectors_count", {
                  count: computedTags?.length,
                })
              }}
            </p>
            <FTagInput
              class="pa-0 rounded-0"
              :tags="computedTags"
              item-value="id"
              item-text="parent_text"
              :default-path="treeDefaultPath"
              machine-icons
              :on-change="(s, l, f, r) => selectMachine(s, r)"
              :on-select-all="onSelectAll"
              :disabled="disabled"
              display-machine-tags
              max-height="9rem"
              flat
            >
            </FTagInput>
            <FButton
              v-if="canSelectMultiple"
              text-class="text-newPrimaryRegular"
              class="align-self-start"
              data-testid="reset-sectors-selector"
              @click="deselectAll"
            >
              {{ $t("global.clear") }}
            </FButton>
          </template>
        </div>

        <v-card-text class="pa-0 flex-1">
          <FDropdownListPerimeters
            item-value="id"
            item-text="parent_text"
            wrapper-class="cursor-pointer"
            :field-text="computedSelectedMachine"
            :placeholder="$t('Profile.select_sector')"
            :selected-sectors="computedTags"
            :default-path="treeDefaultPath"
            :can-select-all="canSelectAll"
            :multi-select="canSelectMultiple"
            :small="small"
            :disabled="disabled"
            display-machine-tags
            outlined
            input
            block
            toggle-default
            keep-scrollbar
            aggregated-tooltips
            hide-btn
            full
            data-testid="tree-dropdown-selector"
            :on-change="(s, l, f, r) => selectMachine(s, r)"
            :on-change-multiple="selectSectorGroup"
            :on-select-all="onSelectAll"
            :on-reset-all="deselectAll"
          />
        </v-card-text>

        <v-card-actions class="pa-0">
          <FButton @click="handleReset">
            {{ $t("global.cancel") }}
          </FButton>
          <FButton
            :disabled="isDisabledConfirmButton"
            filled
            data-testid="validate-sectors-selector"
            @click="handleConfirm"
          >
            {{ $t("global.valider") }}
          </FButton>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<style scoped lang="scss">
.v-card.sectors-selector__card {
  display: flex;
  flex-direction: column;

  &:deep() {
    .v-card-text {
      overflow: scroll;
    }
  }

  .dropdown-list--perimeters {
    padding: 24px 0;
  }
}

.sectors-selector__button-text {
  max-width: 165px;
  display: inline-block;
}
</style>
